import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip
} from '@mui/material';
import { keyframes } from '@mui/material/styles';
import React, { useState } from 'react';
import { config as layoutConfig } from '../pedigree/config';

// ARRANGE BEACON pulse — a soft expanding ring in the pedigree teal accent. Deliberately
// gentle (no flashing colour swap): it should read as "worth a look when you pause", not
// demand attention mid-draw.
const beaconPulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(43, 169, 191, 0.5); }
  70% { box-shadow: 0 0 0 9px rgba(43, 169, 191, 0); }
  100% { box-shadow: 0 0 0 0 rgba(43, 169, 191, 0); }
`;

// Phase 2C — Auto-arrange. The one explicit action that discards the user's manual layout
// (the established within-generation order AND any absolute position pins) and lets the
// engine re-optimise from scratch. With ENABLE_ESTABLISHED_ORDER on, the next layout
// re-captures a fresh optimiser-settled order and re-establishes it — so "auto-arrange"
// means "re-run the optimiser, then sit-and-forget the new arrangement", not "stay unlocked".
// Confirms first; with ENABLE_UNDO_REDO the step is undoable from the toolbar.
function AutoArrange({
  establishedOrder,
  customPositions,
  setEstablishedOrder,
  setCustomPositions,
  setOrderLocked = null,
  orientationSeedRef = null,
  onHistoryCheckpoint = null,
  arrangeHint = false
}) {
  const [open, setOpen] = useState(false);

  const hasManualLayout = Boolean(
    establishedOrder?.size || customPositions?.size
  );

  const handleConfirm = () => {
    // UNDO checkpoint before the clears — undoing an Auto-arrange restores the
    // manual order/pins it discarded (one step for all three states).
    if (onHistoryCheckpoint) onHistoryCheckpoint();
    // D7 orientation anchor: stash the order we are about to clear so the unlocked
    // re-optimise keeps the displayed handedness — adopting the tidier arrangement
    // must not ALSO mirror-flip the whole chart (the beacon promised "tidier",
    // not "backwards").
    if (orientationSeedRef && establishedOrder?.size) {
      orientationSeedRef.current = new Map(establishedOrder);
    }
    // Clear BOTH maps: an empty order makes the engine run the full reorder search again,
    // and clearing pins stops buildRenderModel overriding the re-optimised positions.
    // The result is the CANONICAL from-scratch layout — "the layout as it was drawn" —
    // deterministic, so it also reproduces identically on every reopen.
    if (setEstablishedOrder) setEstablishedOrder(new Map());
    if (setCustomPositions) setCustomPositions(new Map());
    // Back to "as-drawn" mode: drawing keeps re-optimising instead of freezing
    // this arrangement; the settle is still captured + persisted for reopen.
    if (setOrderLocked) setOrderLocked(false);
    setOpen(false);
  };

  return (
    <>
      <Tooltip
        title={
          arrangeHint
            ? 'A tidier arrangement is available — Auto-arrange to apply it'
            : hasManualLayout
            ? 'Auto-arrange (re-optimise the layout)'
            : 'Nothing to re-arrange'
        }
      >
        <span>
          <IconButton
            size="small"
            onClick={() => setOpen(true)}
            data-va-anchor="pedigree.toolbar.autoArrange"
            disabled={!hasManualLayout}
            sx={
              arrangeHint
                ? { animation: `${beaconPulse} 1.8s ease-out infinite` }
                : undefined
            }
          >
            <AutoFixHighIcon
              sx={{
                fontSize: 22,
                color: arrangeHint
                  ? '#2ba9bf'
                  : hasManualLayout
                  ? '#3d4551'
                  : undefined
              }}
            />
          </IconButton>
        </span>
      </Tooltip>

      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Auto-arrange this pedigree?</DialogTitle>
        {/* MUI zeroes a DialogContent's top padding when it follows a DialogTitle via
            `.MuiDialogTitle-root + .MuiDialogContent-root` (specificity 0,2,0), which beats a
            plain sx class. The `&&` doubles the sx class's specificity to match + win on source
            order, so the body text gets a gap below the (dark) header bar. */}
        <DialogContent sx={{ '&&': { pt: 3 } }}>
          <DialogContentText>
            This discards your manual ordering and any positions you have moved,
            then re-optimises the whole pedigree automatically.{' '}
            {layoutConfig.ENABLE_UNDO_REDO
              ? 'You can undo this from the toolbar.'
              : 'This cannot be undone.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirm} color="error" variant="contained">
            Auto-arrange
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default AutoArrange;
